import { inject, injectable } from 'inversify'
import { interval, Subscription, switchMap } from 'rxjs'

import { InjectionTokens } from '@/controller/tokens'

import { SessionExpired } from '@/domain/events/SessionExpired'
import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import { IService } from '../IService'

import { I18n } from '@/i18n/I18n'
import { readAuthCookie } from '@/infra/http/authCookie'
import type { ILogger } from '@/infra/logger/ILogger'
import { ModalUtil } from '@/utils/modalUtil'

@injectable()
export class Authenticator implements IService {
  private _subscriptionChecker!: Subscription
  private _sessionExpiredSubscription: Subscription | null = null
  private _isSessionExpiredModalOpen = false
  private _syncTokenFromCookie = () => {
    const cookieToken = readAuthCookie()
    if (!cookieToken) return
    if (cookieToken !== this.authState.getStateSnapshot().token) {
      this.logger.log('Authenticator', 'Token do cookie mudou; atualizando a sessão em memória')
      this.authState.setToken(cookieToken)
    }
  }
  constructor(
    @inject(InjectionTokens.AuthState) private authState: AuthState,
    @inject(InjectionTokens.I18n) private i18n: I18n,
    @inject(InjectionTokens.Logger) private logger: ILogger
  ) {}

  async init(): Promise<Result<void>> {
    const authToken = this._getAuthenticationCookie()
    if (!authToken) {
      return Result.fail('AUTHENTICATION_COOKIE_NOT_FOUND')
    } else {
      this.authState.setToken(authToken)
    }

    return Result.ok()
  }
  async start(): Promise<Result<void>> {
    if (!this._checkSubscriptionStatus()) {
      return Result.fail('SUBSCRIPTION_ERROR')
    }

    // O token só era lido no boot. Como o cookie `_nat` é de domínio compartilhado com o app legado,
    // ele pode rotacionar enquanto esta aba está aberta — releitura ao voltar para a aba resolve o
    // caso comum antes de qualquer requisição falhar.
    window.addEventListener('focus', this._syncTokenFromCookie)
    document.addEventListener('visibilitychange', this._syncTokenFromCookie)

    // O AxiosHttpClient já tenta se recuperar relendo o cookie; quando não há token novo, a sessão
    // acabou de verdade e o usuário precisa saber — antes disso ele só via "erro ao salvar".
    this._sessionExpiredSubscription = SessionExpired.subscribe(() => this._handleSessionExpired())

    this._subscriptionChecker = interval(1000 * 60 * 5) // 5 minutos
      .pipe(switchMap(async () => this._checkSubscriptionStatus()))
      .subscribe((status) => {
        if (!status) {
          ModalUtil.global.show({
            title: this.i18n.resolveToken('MODAL_SUBSCRIPTION_ACCOUNT-PROBLEM_TITLE'),
            headerDescription: this.i18n.resolveToken('MODAL_SUBSCRIPTION_ACCOUNT-PROBLEM_DESCRIPTION'),
            positiveLabel: this.i18n.resolveToken('MODAL_SUBSCRIPTION_ACCOUNT-PROBLEM_BUTTON'),
            onPositive: () => {
              document.location = import.meta.env.VITE_SUBSCRIPTION_MANAGER_URL
            }
          })
        }
      })

    return Result.ok()
  }

  stop(): Result<void> {
    window.removeEventListener('focus', this._syncTokenFromCookie)
    document.removeEventListener('visibilitychange', this._syncTokenFromCookie)

    if (this._sessionExpiredSubscription) {
      this._sessionExpiredSubscription.unsubscribe()
      this._sessionExpiredSubscription = null
    }

    if (this._subscriptionChecker) {
      this._subscriptionChecker.unsubscribe()
      return Result.ok()
    } else {
      return Result.fail('SubscriptionChecker not found')
    }
  }

  private _handleSessionExpired() {
    if (this._isSessionExpiredModalOpen) return
    this._isSessionExpiredModalOpen = true

    this.logger.warn('Authenticator', 'Sessão expirada: a API rejeitou o token e o cookie não tem token novo')

    ModalUtil.global.show({
      title: this.i18n.resolveToken('MODAL_SESSION-EXPIRED_TITLE'),
      headerDescription: this.i18n.resolveToken('MODAL_SESSION-EXPIRED_DESCRIPTION'),
      positiveLabel: this.i18n.resolveToken('MODAL_SESSION-EXPIRED_BUTTON'),
      onPositive: () => {
        this._isSessionExpiredModalOpen = false
        document.location.reload()
      }
    })
  }

  private _getAuthenticationCookie() {
    return readAuthCookie()
  }

  private _checkSubscriptionStatus(): boolean {
    const currentUser = this.authState.getStateSnapshot().user
    const inTrialCheck = !currentUser?.trialChecked
    const inSubscriptionCheck = currentUser?.subscriptionId && !currentUser?.subscriptionChecked
    const subscriptionDown = ['unpaid', 'canceled', 'ended'].includes(currentUser?.subscriptionStatus ?? '')

    // Valid subscriptions
    const inTrial = !currentUser?.subscriptionId && new Date() <= new Date(currentUser?.trialTo) && !inTrialCheck
    const subscriptionDownButHaveTime =
      subscriptionDown && new Date() <= new Date(currentUser?.subscriptionPeriodEnd) && !inSubscriptionCheck
    const subscriptionOk =
      ['paid', 'pending_payment', 'waiting_payment'].includes(currentUser?.subscriptionStatus ?? '') &&
      !inSubscriptionCheck

    // Manager
    let managerOk = false
    if (currentUser?.managerId) {
      const managerSubscriptionDown = ['unpaid', 'canceled', 'ended'].includes(
        currentUser?.manager?.subscriptionStatus ?? ''
      )
      const managerInTrial =
        !currentUser?.manager?.subscriptionId && new Date() <= new Date(currentUser?.manager?.trialTo)
      const managerSubscriptionDownButHaveTime =
        managerSubscriptionDown && new Date() <= new Date(currentUser?.manager?.subscriptionPeriodEnd)
      const managerSubscriptionOk = ['paid', 'pending_payment', 'waiting_payment'].includes(
        currentUser?.manager?.subscriptionStatus ?? ''
      )

      managerOk = managerInTrial || managerSubscriptionDownButHaveTime || managerSubscriptionOk
    }

    if (inTrial || subscriptionDownButHaveTime || subscriptionOk || managerOk) {
      return true
    } else {
      return false
    }
  }
}
